/**
 * M4 CONTROL-FEEL (the AAA-15/16 surface, minimal cut): live feel-tuning panel.
 * Gated on `?tune=1`. Plain fixed-position DOM — right edge, design-system mono
 * (--bm-mono), one range slider + live value per entry. Entries mutate live
 * objects directly via get/set closures (stickParams, camera taus), so changes
 * apply on the next frame with no plumbing. Settle final defaults in a playtest,
 * then bake them into the constants.
 */

export interface TuneEntry {
  label: string;
  min: number;
  max: number;
  step: number;
  get: () => number;
  set: (v: number) => void;
}

export interface TuneGroup {
  label: string;
  entries: TuneEntry[];
}

const PANEL_CSS =
  'position:fixed;top:120px;right:12px;z-index:60;display:flex;flex-direction:column;' +
  'gap:8px;padding:10px 12px;background:rgba(var(--bm-void-rgb),0.92);' +
  'border:1px solid var(--bm-support);color:var(--bm-mourn-cyan);' +
  "font:11px var(--bm-mono, 'IBM Plex Mono', monospace);min-width:250px;";

/** Build one labelled slider row bound to a TuneEntry. */
const buildRow = (e: TuneEntry): HTMLElement => {
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;flex-direction:column;gap:2px;';
  const head = document.createElement('span');
  head.style.cssText = 'display:flex;justify-content:space-between;';
  const name = document.createElement('span');
  name.textContent = e.label;
  const val = document.createElement('span');
  val.style.color = 'var(--bm-primary)';
  val.textContent = `${e.get()}`;
  head.append(name, val);
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = `${e.min}`;
  slider.max = `${e.max}`;
  slider.step = `${e.step}`;
  slider.value = `${e.get()}`;
  slider.addEventListener('input', () => {
    const v = Number(slider.value);
    e.set(v);
    val.textContent = `${v}`;
  });
  row.append(head, slider);
  return row;
};

const buildSection = (group: TuneGroup): HTMLElement => {
  const section = document.createElement('section');
  section.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
  const title = document.createElement('div');
  title.textContent = group.label;
  title.style.cssText =
    'color:var(--bm-warning);letter-spacing:0.12em;padding-top:2px;border-top:1px solid rgba(var(--bm-primary-rgb),0.18);';
  section.appendChild(title);
  for (const entry of group.entries) section.appendChild(buildRow(entry));
  return section;
};

/** Mount the panel iff the URL carries ?tune=1. Safe to call unconditionally. */
export const initTunePanel = (groups: TuneGroup[]): void => {
  if (new URLSearchParams(window.location.search).get('tune') !== '1') return;
  const panel = document.createElement('div');
  panel.id = 'hud-tune';
  panel.style.cssText = PANEL_CSS;
  const title = document.createElement('div');
  title.textContent = 'FEEL TUNING';
  title.style.cssText = 'color:var(--bm-warning);letter-spacing:0.12em;';
  panel.appendChild(title);
  for (const group of groups) panel.appendChild(buildSection(group));
  document.body.appendChild(panel);
};
